import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../store/slices/authSlice';
import axios from 'axios';

const api = axios.create({
  baseURL: '/api/v1',
  headers: { 'Content-Type': 'application/json' }
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

function HeaderBar({ tenantId }) {
  const { user } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const [credits, setCredits] = useState(0);
  const [usage, setUsage] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (tenantId) fetchBillingData();
  }, [tenantId]);

  const fetchBillingData = async () => {
    try {
      const [balanceRes, usageRes] = await Promise.all([
        api.get(`/billing/balance/${tenantId}`, { headers: { 'x-tenant-id': tenantId } }),
        api.get(`/usage/stats`, { headers: { 'x-tenant-id': tenantId } })
      ]);
      setCredits(balanceRes.data.data?.available || 0);
      setUsage(usageRes.data.data?.thisMonth?.totalCalls || 0);
    } catch (error) {
      console.error('Failed to fetch billing:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCredits = () => {
    navigate(`/client-admin/${tenantId}/billing`);
  };

  const isAdminRole = ['superadmin', 'admin', 'saas-admin', 'client-admin', 'owner'].includes(user?.role);

  if (!isAdminRole) return null;

  return (
    <div className="bg-gray-900 text-white border-b border-gray-800">
      <div className="flex items-center justify-between px-4 py-2">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm text-gray-400">Credits:</span>
            <span className="font-semibold text-green-400">${credits.toFixed(2)}</span>
          </div>
          
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <span className="text-sm text-gray-400">This Month:</span>
            <span className="font-semibold text-blue-400">{usage.toLocaleString()} calls</span>
          </div>
        </div>

        <button
          onClick={handleAddCredits}
          className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 rounded text-sm font-medium transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Credits
        </button>
      </div>
    </div>
  );
}

function TopBar() {
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const isAdminPage = ['superadmin', 'admin', 'saas-admin', 'client-admin', 'owner'].includes(user?.role);

  const getPageTitle = () => {
    if (location.pathname.includes('super-admin')) return 'Super Admin';
    if (location.pathname.includes('paas-admin')) return 'PAAS Admin';
    if (location.pathname.includes('saas-admin')) return 'SaaS Admin';
    if (location.pathname.includes('client-admin')) return 'Client Admin';
    return 'Dashboard';
  };

  return (
    <div className="bg-white shadow-sm border-b">
      <div className="flex items-center justify-between px-6 py-3">
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-semibold text-gray-800">{getPageTitle()}</h1>
          <span className="text-gray-400">|</span>
          <span className="text-sm text-gray-500">{user?.email}</span>
        </div>
        
        <div className="flex items-center gap-4">
          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
            {user?.role}
          </span>
          <button
            onClick={handleLogout}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
}

function Sidebar({ collapsed = false }) {
  const { user } = useSelector((state) => state.auth);
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { tenantId, paasId, saasId } = useParams();

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const getLinks = () => {
    const baseUrl = location.pathname.split('/').slice(0, 3).join('/');

    switch (user?.role) {
      case 'superadmin':
        return [
          { name: 'Overview', path: '/super-admin/dashboard', icon: 'chart' },
          { name: 'PAAS', path: '/super-admin/dashboard', icon: 'server', subItems: [
            { name: 'All Platforms', path: '/super-admin/platforms' },
            { name: 'Create Platform', path: '/super-admin/platforms/create' },
            { name: 'Platform Admins', path: '/super-admin/admins' }
          ]},
          { name: 'Packages', path: '/super-admin/dashboard?tab=packages', icon: 'box', subItems: [
            { name: 'All Packages', path: '/super-admin/dashboard?tab=packages' },
            { name: 'Add Package', path: '/super-admin/add-package' },
            { name: 'Package Usage', path: '/super-admin/package-usage' }
          ]},
          { name: 'Analytics', path: '/super-admin/dashboard?tab=analytics&sub=platform', icon: 'bar', subItems: [
            { name: 'Platform Overview', path: '/super-admin/dashboard?tab=analytics&sub=platform' },
            { name: 'Revenue', path: '/super-admin/dashboard?tab=analytics&sub=revenue' },
            { name: 'Usage Stats', path: '/super-admin/dashboard?tab=analytics&sub=usage' }
          ]},
          { name: 'Billing', path: '/super-admin/dashboard?tab=billing', icon: 'credit' },
          { name: 'Settings', path: '/super-admin/dashboard?tab=settings', icon: 'settings', subItems: [
            { name: 'General', path: '/super-admin/dashboard?tab=settings' },
            { name: 'Subscriptions', path: '/super-admin/dashboard?tab=settings&sub=subscriptions' }
          ]},
        ];
      case 'admin':
        return [
          { name: 'Overview', path: `/paas-admin/${paasId}`, icon: 'chart' },
          { name: 'SaaS', path: `/paas-admin/${paasId}/saas`, icon: 'cloud', subItems: [
            { name: 'All SaaS Systems', path: `/paas-admin/${paasId}/saas` },
            { name: 'Create SaaS System', path: `/paas-admin/${paasId}/saas/create` },
            { name: 'SaaS Admins', path: `/paas-admin/${paasId}/saas/admins` }
          ]},
          { name: 'Packages', path: `/paas-admin/${paasId}/packages`, icon: 'box', subItems: [
            { name: 'All Packages', path: `/paas-admin/${paasId}/packages` },
            { name: 'Add Package', path: `/paas-admin/${paasId}/add-package` },
            { name: 'Package Usage', path: `/paas-admin/${paasId}/package-usage` }
          ]},
          { name: 'Analytics', path: `/paas-admin/${paasId}?tab=analytics`, icon: 'bar', subItems: [
            { name: 'Overview', path: `/paas-admin/${paasId}?tab=analytics` },
            { name: 'Revenue', path: `/paas-admin/${paasId}?tab=analytics&sub=revenue` },
            { name: 'Usage Stats', path: `/paas-admin/${paasId}?tab=analytics&sub=usage` }
          ]},
          { name: 'Billing', path: `/paas-admin/${paasId}?tab=billing`, icon: 'credit' },
          { name: 'Income', path: `/paas-admin/${paasId}?tab=income`, icon: 'dollar' },
          { name: 'Settings', path: `/paas-admin/${paasId}?tab=settings`, icon: 'settings' },
        ];
      case 'saas-admin':
        return [
          { name: 'Overview', path: `/saas-admin/${saasId}`, icon: 'chart' },
          { name: 'Clients', path: `/saas-admin/${saasId}/clients`, icon: 'users', subItems: [
            { name: 'All Clients', path: `/saas-admin/${saasId}/clients` },
            { name: 'Create Client', path: `/saas-admin/${saasId}/clients/create` },
            { name: 'Client Admins', path: `/saas-admin/${saasId}/clients/admins` }
          ]},
          { name: 'Packages', path: `/saas-admin/${saasId}/packages`, icon: 'box', subItems: [
            { name: 'All Packages', path: `/saas-admin/${saasId}/packages` },
            { name: 'Install Package', path: `/saas-admin/${saasId}/available-packages` },
            { name: 'Package Usage', path: `/saas-admin/${saasId}/package-usage` }
          ]},
          { name: 'Analytics', path: `/saas-admin/${saasId}?tab=analytics`, icon: 'bar', subItems: [
            { name: 'Overview', path: `/saas-admin/${saasId}?tab=analytics` },
            { name: 'Revenue', path: `/saas-admin/${saasId}?tab=analytics&sub=revenue` },
            { name: 'Usage Stats', path: `/saas-admin/${saasId}?tab=analytics&sub=usage` }
          ]},
          { name: 'Billing', path: `/saas-admin/${saasId}?tab=billing`, icon: 'credit' },
          { name: 'Income', path: `/saas-admin/${saasId}?tab=income`, icon: 'dollar' },
          { name: 'Settings', path: `/saas-admin/${saasId}?tab=settings`, icon: 'settings' },
        ];
      case 'client-admin':
      case 'owner':
        return [
          { name: 'Dashboard', path: `/client-admin/${tenantId}`, icon: 'chart' },
          { name: 'Users', path: `/client-admin/${tenantId}?tab=users`, icon: 'users' },
          { name: 'Content', path: `/client-admin/${tenantId}?tab=content`, icon: 'file' },
          { name: 'Packages', path: `/client-admin/${tenantId}/packages`, icon: 'box', subItems: [
            { name: 'All Packages', path: `/client-admin/${tenantId}/packages` },
            { name: 'Activate Package', path: `/client-admin/${tenantId}/available-packages` },
            { name: 'Package Usage', path: `/client-admin/${tenantId}/package-usage` }
          ]},
          { name: 'Analytics', path: `/client-admin/${tenantId}?tab=analytics`, icon: 'bar', subItems: [
            { name: 'Overview', path: `/client-admin/${tenantId}?tab=analytics` },
            { name: 'Usage Stats', path: `/client-admin/${tenantId}?tab=analytics&sub=usage` }
          ]},
          { name: 'Integrations', path: `/client-admin/${tenantId}/integrations`, icon: 'link' },
          { name: 'Billing', path: `/client-admin/${tenantId}/billing`, icon: 'credit' },
          { name: 'Settings', path: `/client-admin/${tenantId}?tab=settings`, icon: 'settings' },
        ];
      default:
        return [
          { name: 'Dashboard', path: '/dashboard', icon: 'chart' },
          { name: 'Content', path: '/content', icon: 'file' },
          { name: 'Profile', path: '/profile', icon: 'user' },
        ];
    }
  };

  const links = getLinks();
  const isCollapsed = collapsed;

  const icons = {
    chart: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    server: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
      </svg>
    ),
    cloud: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
      </svg>
    ),
    users: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    ),
    credit: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
      </svg>
    ),
    settings: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    file: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    user: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
    link: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
      </svg>
    ),
    bar: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    box: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
      </svg>
    ),
    plus: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    ),
    search: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    ),
    dollar: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  };

  const isActive = (path) => location.pathname.includes(path.replace(/\/$/, ''));
  const [expandedMenus, setExpandedMenus] = useState({});

  const toggleMenu = (name) => {
    setExpandedMenus(prev => ({ ...prev, [name]: !prev[name] }));
  };

  return (
    <div className="flex flex-col h-screen bg-gray-900">
      {/* Logo */}
      <div className="h-14 flex items-center justify-center border-b border-gray-800">
        <Link to="/dashboard" className="text-lg font-bold text-white">
          {isCollapsed ? 'M' : 'MultiPaaS'}
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-3 overflow-y-auto">
        {links.map((link) => (
          <div key={link.name}>
            {link.subItems ? (
              <>
                <button
                  onClick={() => toggleMenu(link.name)}
                  className={`flex items-center justify-between w-full px-4 py-2.5 mx-2 rounded-lg transition-all duration-200 ${
                    link.subItems.some(sub => isActive(sub.path))
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                  }`}
                >
                  <div className="flex items-center">
                    <span className={`${isCollapsed ? 'mx-auto' : 'mr-3'}`}>
                      {icons[link.icon]}
                    </span>
                    {!isCollapsed && <span className="text-sm">{link.name}</span>}
                  </div>
                  {!isCollapsed && (
                    <svg className={`w-4 h-4 transition-transform ${expandedMenus[link.name] ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </button>
                {!isCollapsed && expandedMenus[link.name] && (
                  <div className="ml-8 mr-2 mt-1 mb-2 space-y-1">
                    {link.subItems.map((sub) => (
                      <Link
                        key={sub.name}
                        to={sub.path}
                        className={`flex items-center px-3 py-2 rounded-lg text-sm transition-all ${
                          isActive(sub.path)
                            ? 'bg-blue-700 text-white'
                            : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                        }`}
                      >
                        {sub.name}
                      </Link>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <Link
                to={link.path}
                className={`flex items-center px-4 py-2.5 mx-2 rounded-lg transition-all duration-200 ${
                  isActive(link.path.replace(/\/$/, ''))
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                }`}
              >
                <span className={`${isCollapsed ? 'mx-auto' : 'mr-3'}`}>
                  {icons[link.icon]}
                </span>
                {!isCollapsed && <span className="text-sm">{link.name}</span>}
              </Link>
            )}
          </div>
        ))}
      </nav>

      {/* User Section */}
      <div className="border-t border-gray-800 p-3">
        <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-2'}`}>
          <div className="w-7 h-7 rounded-full bg-blue-600 flex items-center justify-center">
            <span className="text-xs font-medium">
              {user?.email?.charAt(0).toUpperCase() || 'U'}
            </span>
          </div>
          {!isCollapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-400 truncate">{user?.email}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AppLayout() {
  const { user } = useSelector((state) => state.auth);
  const { tenantId } = useParams();
  const location = useLocation();
  
  const isAdminPage = user?.role && ['superadmin', 'admin', 'saas-admin', 'client-admin', 'owner'].includes(user.role);

  return (
    <div className="flex min-h-screen bg-gray-100">
      {isAdminPage && <Sidebar />}
      
      <div className="flex-1 flex flex-col">
        {isAdminPage && (
          <>
            <HeaderBar tenantId={tenantId} />
            <TopBar />
          </>
        )}
        
        <main className="flex-1 p-6">
          <slot />
        </main>
      </div>
    </div>
  );
}

export { Sidebar, HeaderBar, TopBar, AppLayout };
export default Sidebar;